import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { Link } from "react-router-dom";
import clsx from "clsx";

// Custom.
import Layout from "containers/Layout/Layout";
import { useStyles } from "./styles";
import { DashboardBoxAction } from "./global";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import VoAuth from "@vocollege/app/dist/modules/VoAuth";
import routes from "pages/routes";

const Dashboard: React.FC = (props) => {
  const classes = useStyles();
  const [accessStatistics, setAccessStatistics] = useState(false);

  // Methods.

  const getBox = (
    title: string,
    description = "",
    actions: DashboardBoxAction[] = []
  ) => {
    return (
      <Grid item xs={12} md={4}>
        <Card elevation={10} className={classes.box}>
          <CardHeader title={title} className={classes.header}></CardHeader>
          <CardContent className={classes.boxContent}>
            <Typography
              variant="subtitle1"
              component="h3"
              className={classes.boxDescription}
            >
              {description}
            </Typography>
          </CardContent>
          {actions && actions.length > 0 && (
            <CardActions className={classes.boxActions}>
              {actions
                .filter((v: DashboardBoxAction) => v.visible)
                .map((v: DashboardBoxAction, i: number) => (
                  <React.Fragment key={i}>
                    {v.url && (
                      <Button
                        size="small"
                        component={Link}
                        to={v.url || ""}
                        color={v.color}
                        variant={v.variant}
                      >
                        {v.label}
                      </Button>
                    )}
                    {!v.url && v.onClick && (
                      <Button
                        component="a"
                        size="small"
                        onClick={v.onClick}
                        color={v.color}
                        variant={v.variant}
                      >
                        {v.label}
                      </Button>
                    )}
                  </React.Fragment>
                ))}
            </CardActions>
          )}
        </Card>
      </Grid>
    );
  };

  // Effects.

  useEffect(() => {
    setAccessStatistics(VoAuth.ability.can("access_statistics", "Cert"));
  }, []);

  return (
    <Layout>
      <div className={clsx(classes.root, "vo-global__top-bottom-space")}>
        <Grid
          spacing={4}
          alignItems="stretch"
          justifyContent="center"
          container
        >
          {VoAuth.ability.can("list_own_tutor_certificates", "Cert") && (
            <Grid item xs={12}>
              <Alert severity="success">
                <strong>Handledarintyg:</strong> Nu finns funktionen klar som
                innebär att intyg kan skrivas ut efter genomförda moduler inom
                handledarutbildningen.
              </Alert>
            </Grid>
          )}

          {VoAuth.ability.can("list_own_diplomas", "Cert") &&
            getBox(
              I18n.get.diploma.labels.diplomas,
              I18n.get.diploma.messages.intro,
              [
                {
                  label: I18n.get.statistics.labels.statistics,
                  url: routes["statistics.diplomas"].path,
                  color: "secondary",
                  variant: "outlined",
                  visible: accessStatistics,
                },
                {
                  label: I18n.get.actions.showAll,
                  url: "/diplom",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can("list_own_diplomas", "Cert"),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/diplom/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can("create_own_diplomas", "Cert"),
                },
              ]
            )}
          {VoAuth.ability.can("list_own_certificates", "Cert") &&
            getBox(
              I18n.get.certificate.labels.certificates,
              I18n.get.certificate.messages.intro,
              [
                {
                  label: I18n.get.statistics.labels.statistics,
                  url: routes["statistics.certificates"].path,
                  color: "secondary",
                  variant: "outlined",
                  visible: accessStatistics,
                },
                {
                  label: I18n.get.actions.showAll,
                  url: "/intyg",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can("list_own_certificates", "Cert"),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/intyg/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_certificates",
                    "Cert"
                  ),
                },
              ]
            )}
          {VoAuth.ability.can("list_own_tutor_certificates", "Cert") &&
            getBox(
              I18n.get.tutorCertificate.labels.tutorCertificates,
              I18n.get.tutorCertificate.messages.intro,
              [
                {
                  label: I18n.get.statistics.labels.statistics,
                  url: routes["statistics.tutorCertificates"].path,
                  color: "secondary",
                  variant: "outlined",
                  visible: accessStatistics,
                },
                {
                  label: I18n.get.actions.showAll,
                  url: "/handledarintyg",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can(
                    "list_own_tutor_certificates",
                    "Cert"
                  ),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/handledarintyg/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_tutor_certificates",
                    "Cert"
                  ),
                },
              ]
            )}
          {VoAuth.ability.can("list_own_care_assistant_diplomas", "Cert") &&
            getBox(
              I18n.get.careAssistantDiploma.labels.careAssistantDiplomas,
              I18n.get.careAssistantDiploma.messages.intro,
              [
                {
                  label: I18n.get.statistics.labels.statistics,
                  url: routes["statistics.careAssistantDiplomas"].path,
                  color: "secondary",
                  variant: "outlined",
                  visible: accessStatistics,
                },
                {
                  label: I18n.get.actions.showAll,
                  url: "/diplom-vardbitrade",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can(
                    "list_own_care_assistant_diplomas",
                    "Cert"
                  ),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/diplom-vardbitrade/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_care_assistant_diplomas",
                    "Cert"
                  ),
                },
              ]
            )}

          {VoAuth.ability.can("list_own_ambassador_certificates", "Cert") &&
            getBox(
              I18n.get.ambassadorCertificate.labels.ambassadorCertificates,
              I18n.get.ambassadorCertificate.messages.intro,
              [
                {
                  label: I18n.get.statistics.labels.statistics,
                  url: routes["statistics.ambassadorCertificates"].path,
                  color: "secondary",
                  variant: "outlined",
                  visible: accessStatistics,
                },
                {
                  label: I18n.get.actions.showAll,
                  url: "/intyg-ambassador",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can(
                    "list_own_ambassador_certificates",
                    "Cert"
                  ),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/intyg-ambassador/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_ambassador_certificates",
                    "Cert"
                  ),
                },
              ]
            )}

          {VoAuth.ability.can("list_own_tutor_award_certificates", "Cert") &&
            getBox(
              I18n.get.tutorAwardCertificate.labels.tutorAwardCertificates,
              I18n.get.tutorAwardCertificate.messages.intro,
              [
                {
                  label: I18n.get.actions.showAll,
                  url: "/certifikat-arets-handledare",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can(
                    "list_own_tutor_award_certificates",
                    "Cert"
                  ),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/certifikat-arets-handledare/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_tutor_award_certificates",
                    "Cert"
                  ),
                },
              ]
            )}
          {VoAuth.ability.can("list_own_college_certificates", "Cert") &&
            getBox(
              I18n.get.collegeCertificate.labels.collegeCertificates,
              I18n.get.collegeCertificate.messages.intro,
              [
                {
                  label: I18n.get.actions.showAll,
                  url: "/college-certifikat",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can(
                    "list_own_college_certificates",
                    "Cert"
                  ),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/college-certifikat/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_college_certificates",
                    "Cert"
                  ),
                },
              ]
            )}
          {VoAuth.ability.can(
            "list_own_language_representative_diplomas",
            "Cert"
          ) &&
            getBox(
              I18n.get.languageRepresentativeDiploma.labels
                .languageRepresentativeDiplomas,
              I18n.get.languageRepresentativeDiploma.messages.intro,
              [
                {
                  label: I18n.get.statistics.labels.statistics,
                  url: routes["statistics.languageRepresentativeDiplomas"].path,
                  color: "secondary",
                  variant: "outlined",
                  visible: accessStatistics,
                },
                {
                  label: I18n.get.actions.showAll,
                  url: "/diplom-sprakombud",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can(
                    "list_own_language_representative_diplomas",
                    "Cert"
                  ),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/diplom-sprakombud/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_language_representative_diplomas",
                    "Cert"
                  ),
                },
              ]
            )}
          {VoAuth.ability.can(
            "list_own_language_representative_educator_diplomas",
            "Cert"
          ) &&
            getBox(
              I18n.get.languageRepresentativeEducatorDiploma.labels
                .languageRepresentativeEducatorDiplomas,
              I18n.get.languageRepresentativeEducatorDiploma.messages.intro,
              [
                {
                  label: I18n.get.statistics.labels.statistics,
                  url: routes[
                    "statistics.languageRepresentativeEducatorDiplomas"
                  ].path,
                  color: "secondary",
                  variant: "outlined",
                  visible: accessStatistics,
                },
                {
                  label: I18n.get.actions.showAll,
                  url: "/diplom-sprakombudsutbildare",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can(
                    "list_own_language_representative_educator_diplomas",
                    "Cert"
                  ),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/diplom-sprakombudsutbildare/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_language_representative_educator_diplomas",
                    "Cert"
                  ),
                },
              ]
            )}
          {VoAuth.ability.can("list_own_course_moments", "Cert") &&
            getBox(
              I18n.get.vocertCourseMoment.label,
              I18n.get.vocertCourseMoment.messages.intro,
              [
                {
                  label: I18n.get.statistics.labels.statistics,
                  url: routes["statistics.courseMoments"].path,
                  color: "secondary",
                  variant: "outlined",
                  visible: accessStatistics,
                },
                {
                  label: I18n.get.actions.showAll,
                  url: "/utbildningar",
                  color: "secondary",
                  variant: "outlined",
                  visible: VoAuth.ability.can(
                    "list_own_course_moments",
                    "Cert"
                  ),
                },
                {
                  label: I18n.get.actions.create,
                  url: "/utbildningar/skapa",
                  color: "secondary",
                  variant: "contained",
                  visible: VoAuth.ability.can(
                    "create_own_course_moments",
                    "Cert"
                  ),
                },
              ]
            )}
        </Grid>
      </div>
    </Layout>
  );
};

export default Dashboard;
